import type {
  ApolloCache,
  ApolloClientOptions,
  NormalizedCacheObject,
} from '@apollo/client';
import { ApolloClient, ApolloLink, InMemoryCache, createHttpLink } from '@apollo/client';
import React from 'react';
import { useAuth } from '@auth/oauth/AuthProvider';
import { createApolloAuthLink } from './apolloAuthLink';

export type UseApolloAuthClientOptions = {
  /**
   * An ApolloClient created outside this hook (e.g. a module-scoped
   * singleton wired into non-React setup code, or a client shared across
   * multiple hooks) whose link chain should have the shared auth link
   * spliced in, instead of building a net-new client.
   *
   * When provided, `uri`/`link`/`cache`/`clientOptions` are ignored — this
   * hook only attaches to the existing instance and returns it as-is.
   */
  existingClient?: ApolloClient<unknown>;
  /**
   * GraphQL endpoint for the terminating HttpLink. Required for a net-new
   * client unless `link` is provided instead.
   */
  uri?: string;
  /**
   * An already-assembled terminating link chain (e.g. an HttpLink composed
   * with batching/retry/error links) to use instead of the plain HttpLink
   * built from `uri`. Takes precedence over `uri` when both are given.
   */
  link?: ApolloLink;
  /** Cache for a net-new client. Defaults to `new InMemoryCache()`. */
  cache?: ApolloCache<NormalizedCacheObject>;
  /**
   * Additional ApolloClient constructor options for a net-new client
   * (e.g. `name`, `version`, `connectToDevTools`). `link` and `cache` here
   * are ignored in favor of the dedicated options above.
   */
  clientOptions?: Partial<
    Omit<ApolloClientOptions<NormalizedCacheObject>, 'link' | 'cache'>
  >;
  /**
   * When `false`, skips attaching auth entirely — the client (net-new or
   * existing) keeps/reverts to its plain terminating link chain, with no
   * auth link spliced in.
   *
   * Exists so callers that must call this hook unconditionally — e.g. to
   * satisfy the rules of hooks while a feature flag decides whether this
   * hook or a legacy auth link should own auth-attaching for a given
   * render — can flip attachment on/off without conditionally calling the
   * hook itself. Defaults to `true`.
   */
  enabled?: boolean;
};

/**
 * Creates (or, when `existingClient` is provided, attaches to) an
 * ApolloClient that attaches auth credentials using the same three-tier
 * fallback strategy as `useAuthClient` / `createApolloAuthLink`:
 *
 *  1. **Auth0 access token** (Authorization: Bearer) — preferred.
 *  2. **Checkout access token** (Authorization: Bearer) — guest checkout fallback.
 *  3. **credentials: 'include'** — relies on the terminating HttpLink to send
 *     session cookies when neither token is available.
 *
 * ## Net-new Apollo clients
 *
 * Call the hook with a `uri` (or a custom terminating `link`, e.g. one that
 * already includes batching/retry/error links) and it builds and memoises
 * the client for you — mirroring `useAuthClient`'s "create one if you don't
 * hand me an existing one" behavior:
 *
 * ```tsx
 * const client = useApolloAuthClient({ uri: 'https://api.onepeloton.com/graphql' });
 * return <ApolloProvider client={client}>{children}</ApolloProvider>;
 * ```
 *
 * ## Module-scoped / non-React ApolloClient singletons
 *
 * If a client is already constructed outside the React tree (e.g. wired
 * into Redux saga context), mount this hook once near the root of the app
 * and pass the singleton as `existingClient` — the hook splices the auth
 * link ahead of the client's existing link chain via `client.setLink()` and
 * returns that same instance:
 *
 * ```tsx
 * useApolloAuthClient({ existingClient: sharedApolloClient });
 * ```
 *
 * As with `useAuthClient`, the auth link always resolves to the freshest
 * token available at request time — the link's identity is stable across
 * renders (auth state is read through a ref internally), so logging in
 * mid-session doesn't require rebuilding the client or re-attaching
 * anything. Toggling `enabled` uses `setLink` in place rather than
 * recreating the client, so a net-new client's cache survives the toggle.
 */
export const useApolloAuthClient = ({
  existingClient,
  uri,
  link,
  cache,
  clientOptions,
  enabled = true,
}: UseApolloAuthClientOptions = {}): ApolloClient<NormalizedCacheObject> => {
  const { getAccessToken } = useAuth();

  const getAccessTokenRef = React.useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  const authLink = React.useMemo(
    () => createApolloAuthLink(() => getAccessTokenRef.current()),
    [],
  );

  const clientOptionsSerialized = JSON.stringify(clientOptions ?? {});

  // Build (or reuse) the client with its plain terminating link chain —
  // auth is spliced in separately below via setLink, so toggling `enabled`
  // never requires reconstructing the client (and losing its cache).
  const client = React.useMemo((): ApolloClient<NormalizedCacheObject> => {
    if (existingClient) {
      return existingClient as ApolloClient<NormalizedCacheObject>;
    }

    const terminatingLink =
      link ?? (uri ? createHttpLink({ uri, credentials: 'include' }) : null);

    if (!terminatingLink) {
      throw new Error(
        'useApolloAuthClient: either `existingClient`, `link`, or `uri` must be provided.',
      );
    }

    const parsedClientOptions: Partial<
      Omit<ApolloClientOptions<NormalizedCacheObject>, 'link' | 'cache'>
    > = JSON.parse(clientOptionsSerialized);

    return new ApolloClient({
      link: terminatingLink,
      cache: cache ?? new InMemoryCache(),
      ...parsedClientOptions,
    });
  }, [existingClient, uri, link, cache, clientOptionsSerialized]);

  const originalLinkRef = React.useRef<{
    client: ApolloClient<NormalizedCacheObject>;
    link: ApolloLink;
  } | null>(null);

  if (originalLinkRef.current?.client !== client) {
    originalLinkRef.current = { client, link: client.link };
  }

  const { link: originalLink } = originalLinkRef.current;

  const desiredLink = React.useMemo(
    () => (enabled ? ApolloLink.from([authLink, originalLink]) : originalLink),
    [enabled, authLink, originalLink],
  );

  if (client.link !== desiredLink) {
    client.setLink(desiredLink);
  }

  return client;
};
